import React, { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupportTicket, deleteSupportTicket } from '../../api/support';
import { Card, Typography, Button, Space, message, Descriptions, Modal, Row, Col } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { SupportTicket } from '../../api/support';
import SupportTicketModal from '../../components/features/support/SupportTicketModal';

const { Title } = Typography;

const SupportTicketDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stateTicket = location.state?.ticket as SupportTicket | undefined;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: ticket, isLoading, refetch } = useQuery<SupportTicket | null>({
    queryKey: ['supportTicket', id],
    queryFn: () => getSupportTicket(id!),
    enabled: !stateTicket && !!id,
    initialData: stateTicket,
    staleTime: 5 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSupportTicket(id),
    onSuccess: () => {
      message.success('Support ticket deleted');
      queryClient.invalidateQueries({ queryKey: ['supportTickets'] });
      navigate('/support');
    },
    onError: () => message.error('Failed to delete support ticket'),
  });

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Support Ticket',
      content: 'Are you sure you want to delete this support ticket?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => ticket && deleteMutation.mutate(ticket.id),
    });
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    refetch();
    message.success('Support ticket updated');
  };

  if (isLoading || !ticket) return <Card loading />;

  return (
    <div style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
            <Title level={3} style={{ margin: 0 }}>{ticket.subject}</Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>Edit</Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>Delete</Button>
          </Space>
        </Col>
      </Row>
      <Card>
        <Descriptions bordered column={1} size="middle">
          <Descriptions.Item label="Description">{ticket.description}</Descriptions.Item>
          <Descriptions.Item label="Status">{ticket.status}</Descriptions.Item>
          <Descriptions.Item label="Priority">{ticket.priority}</Descriptions.Item>
          <Descriptions.Item label="Assigned To">{ticket.assigned_to || '-'}</Descriptions.Item>
          <Descriptions.Item label="Created At">{ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '-'}</Descriptions.Item>
          <Descriptions.Item label="Updated At">{ticket.updated_at ? new Date(ticket.updated_at).toLocaleString() : '-'}</Descriptions.Item>
          <Descriptions.Item label="Customer ID">{ticket.customer_id || '-'}</Descriptions.Item>
          <Descriptions.Item label="Workspace Name">{ticket.workspace_name || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>
      <SupportTicketModal
        open={isEditModalOpen}
        ticket={ticket}
        onSuccess={handleEditSuccess}
        onCancel={() => setIsEditModalOpen(false)}
        destroyOnHidden
      />
    </div>
  );
};

export default SupportTicketDetailsPage; 